import { curveMonotoneX } from '@visx/curve';
import { useParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { useId } from 'react';
import { cn } from '../../lib/cn.ts';
import { Tooltip } from '../Tooltip.tsx';
import type { DataSlot } from './PulseChart.tsx';

export interface BurnChartProps {
  /** Running total at the end of each day so far, oldest first (index 0 is the first day). */
  actual: readonly number[];
  /** Days in the whole period (the x-axis runs to its end). */
  days: number;
  /** The projected total at the end of the period: a dashed line from today. */
  forecast?: number | null;
  /** The limit: a dashed line with its label, never a red zone (data-viz.md). */
  budget?: number | null;
  format: (n: number) => string;
  /** Short tick labels on the value axis: "$600". */
  axisFormat?: (n: number) => string;
  /** The date of day i: "Oct 4". */
  dayLabel: (i: number) => string;
  /** Plot height in px, axis labels included. */
  height?: number;
  slot?: DataSlot;
  /** Accessible summary: "$331 spent of $600 by Oct 18; forecast $597 by Oct 31". */
  label: string;
  /** Fixed width instead of measuring the container (tests). */
  width?: number;
  className?: string;
}

const AXIS_W = 48;

/** A top for the value axis whose quarters are round: 1, 1.2, 1.6, 2, 2.4, 3, 4, 5, 6 or 8 times a power of ten. */
export function burnTop(v: number): number {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 1.2, 1.6, 2, 2.4, 3, 4, 5, 6, 8, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}
const AXIS_H = 24;

/**
 * Will we stay in budget (data-viz.md, "Burn line"): the running total as a line with a soft
 * area, the forecast as a dashed line on to the period's end, the budget as a dashed rule with
 * its label, and a today keyline. The line draws itself once; hover any day for its numbers.
 */
export function BurnChart({ actual, days, forecast, budget, format, axisFormat = format, dayLabel, height = 240, slot = 1, label, width: fixed, className }: BurnChartProps) {
  const gid = `burn${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const { parentRef, width: measured } = useParentSize({ initialSize: { width: fixed ?? 640 }, debounceTime: 50 });
  const width = Math.max(160, fixed ?? measured);
  const colour = `var(--data-${slot})`;
  const total = Math.max(1, days);
  const today = actual.length - 1;
  const spent = actual[today] ?? 0;
  const top = burnTop(Math.max(budget ?? 0, forecast ?? 0, spent, 1) * 1.08);
  const plotH = height - AXIS_H;
  const x = scaleLinear<number>({ domain: [0, total - 1], range: [AXIS_W + 4, width - 4] });
  const y = scaleLinear<number>({ domain: [0, top], range: [plotH - 1, 6] });
  const ticks = [0, top / 4, top / 2, (top * 3) / 4, top];
  const pts = actual.map((v, i) => [i, v] as const);
  const fc = forecast ?? 0;
  const showForecast = forecast != null && today >= 0 && today < total - 1;
  const step = x(1) - x(0);
  const xTicks = [0, today, total - 1].filter((t, i, a) => t >= 0 && a.indexOf(t) === i && (t === today || Math.abs(x(t) - x(today)) > 56));
  const dailyOf = (i: number) => (actual[i] ?? 0) - (i > 0 ? (actual[i - 1] ?? 0) : 0);

  return (
    <div ref={parentRef} className={cn('relative min-w-0', className)} style={{ height }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} className="absolute inset-0 overflow-visible">
        <defs>
          <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={colour} stopOpacity="0.3" />
            <stop offset="100%" stopColor={colour} stopOpacity="0" />
          </linearGradient>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={AXIS_W} x2={width} y1={y(t)} y2={y(t)} stroke="var(--border-subtle)" strokeDasharray={t === 0 ? undefined : '2 4'} />
            <text x={AXIS_W - 10} y={y(t)} dy="0.32em" textAnchor="end" fill="var(--fg-subtle)" className="text-caption num">
              {axisFormat(t)}
            </text>
          </g>
        ))}
        {today >= 0 && <line x1={x(today)} x2={x(today)} y1={4} y2={plotH} stroke="var(--border-strong)" strokeDasharray="1 3" />}
        {budget != null && budget > 0 && (
          <g>
            <line x1={AXIS_W} x2={width} y1={y(budget)} y2={y(budget)} stroke="var(--fg-subtle)" strokeDasharray="5 4" />
            <text x={width - 2} y={y(budget) - 6} textAnchor="end" fill="var(--fg-muted)" className="text-caption num">
              Budget {axisFormat(budget)}
            </text>
          </g>
        )}
        {pts.length > 1 && (
          <>
            <AreaClosed data={pts as unknown as [number, number][]} x={(d) => x(d[0])} y={(d) => y(d[1])} yScale={y} curve={curveMonotoneX} fill={`url(#${gid})`} />
            <LinePath
              className="motion-draw"
              pathLength={1}
              strokeDasharray="1"
              data={pts as unknown as [number, number][]}
              x={(d) => x(d[0])}
              y={(d) => y(d[1])}
              curve={curveMonotoneX}
              stroke={colour}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </>
        )}
        {showForecast && (
          <g>
            <line x1={x(today)} y1={y(spent)} x2={x(total - 1)} y2={y(fc)} stroke={colour} strokeOpacity={0.7} strokeWidth={1.5} strokeDasharray="4 4" strokeLinecap="round" />
            <circle cx={x(total - 1)} cy={y(fc)} r={3} fill="var(--surface)" stroke={colour} strokeWidth={1.5} />
            <text x={x(total - 1) - 8} y={y(fc) + (budget && Math.abs(y(fc) - y(budget)) < 20 ? 24 : -10)} textAnchor="end" fill="var(--fg-muted)" className="text-caption num">
              {format(fc)} forecast
            </text>
          </g>
        )}
        {today >= 0 && (
          <>
            <circle cx={x(today)} cy={y(spent)} r={7} fill={colour} opacity={0.18} />
            <circle cx={x(today)} cy={y(spent)} r={3.5} fill={colour} stroke="var(--surface)" strokeWidth={1.5} />
          </>
        )}
        {xTicks.map((t) => (
          <text
            key={t}
            x={x(t)}
            y={height - 6}
            textAnchor={t === 0 ? 'start' : t === total - 1 ? 'end' : 'middle'}
            fill={t === today ? 'var(--fg-muted)' : 'var(--fg-subtle)'}
            className="text-caption num"
          >
            {t === today ? 'Today' : dayLabel(t)}
          </text>
        ))}
      </svg>
      {/* Hover targets, one per elapsed day, for the exact numbers. */}
      <div className="absolute top-0 flex" style={{ left: x(0) - step / 2, width: step * actual.length, height: plotH }}>
        {actual.map((v, i) => (
          <Tooltip
            key={i}
            side="top"
            content={
              <span className="flex flex-col gap-0.5">
                <span className="font-medium">{dayLabel(i)}</span>
                <span className="text-fg-muted">
                  <span className="text-fg num">{format(v)}</span> so far
                </span>
                <span className="text-fg-muted">
                  <span className="text-fg num">{format(dailyOf(i))}</span> that day
                </span>
              </span>
            }
          >
            <span tabIndex={-1} className="h-full min-w-0 flex-1 rounded-control transition-colors duration-(--duration-fast) hover:bg-hover" />
          </Tooltip>
        ))}
      </div>
    </div>
  );
}
